import {
  Component,
  ElementRef,
  computed,
  effect,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'app-snap-carousel',
  standalone: true,
  templateUrl: './snap-carousel.component.html',
})
export class SnapCarouselComponent {
  /** Number of slides — drives the dot indicators. */
  readonly count = input.required<number>();
  /** Extra classes on the scroll track (e.g. responsive grid breakouts). */
  readonly trackClass = input('');
  /** Extra classes on the dots row (e.g. `sm:hidden`). */
  readonly dotsClass = input('');
  /** Accessible label for the tablist. */
  readonly ariaLabel = input('Carousel');
  /** Prefix for each dot's aria-label, e.g. "Show photo". */
  readonly dotLabel = input('Show item');
  /** Change this to reset the carousel (e.g. product id). */
  readonly resetKey = input<unknown>(0);
  /** Overlay prev/next arrows on md+ (product gallery). */
  readonly showArrows = input(false);

  readonly active = signal(0);
  private readonly track = viewChild<ElementRef<HTMLElement>>('track');

  readonly indices = computed(() => Array.from({ length: Math.max(0, this.count()) }, (_, i) => i));

  constructor() {
    effect(() => {
      this.resetKey();
      this.count();
      untracked(() => {
        this.active.set(0);
        const el = this.track()?.nativeElement;
        if (el) el.scrollLeft = 0;
      });
    });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el.clientWidth) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    this.active.set(Math.min(Math.max(index, 0), Math.max(this.count() - 1, 0)));
  }

  goTo(index: number): void {
    const track = this.track()?.nativeElement;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
    this.active.set(index);
  }

  prev(): void {
    const total = this.count();
    if (total < 2) return;
    this.goTo((this.active() - 1 + total) % total);
  }

  next(): void {
    const total = this.count();
    if (total < 2) return;
    this.goTo((this.active() + 1) % total);
  }
}
